import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { motion } from 'framer-motion';
import { 
  Building2, UserRound, Phone, Activity, ChevronDown, 
  HeartPulse, Pill, Brain, Bone, Eye, Award, ArrowRight, Video, 
  MessageSquareQuote, HelpCircle, ShieldCheck, CreditCard, Banknote, CalendarCheck, MapPin, Sparkles, Star
} from 'lucide-react';

import hospitalApiService from '../../services/hospitalApiService';

// ─── Color tokens (V2) ──────────────────────────────────────────────────────
const C = {
  navy:    '#0A0A0A',
  navy2:   '#171717',
  navy3:   '#262626',
  teal:    '#2563EB',
  tealLt:  '#EFF6FF',
  tealMid: '#3B82F6',
  bg:      '#FAFAFA',
  border:  'rgba(0, 0, 0, 0.08)',
  text:    '#171717',
  text2:   '#525252',
  text3:   '#737373',
  white:   '#FFFFFF',
  gold:    '#EAB308',
  goldLt:  '#FFFBEB'
};

const fadeUp: any = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger: any = { visible: { transition: { staggerChildren: 0.1 } } };

function formatPrice(p: number) {
  if (p >= 100000) return `₹${(p / 100000).toFixed(p % 100000 === 0 ? 0 : 1)}L`;
  return `₹${p.toLocaleString('en-IN')}`;
}

const getIcon = (emoji: string) => {
  const map: Record<string, any> = { '❤️': HeartPulse, '🧠': Brain, '🦴': Bone, '👁️': Eye, '🦷': Sparkles, '👶': UserRound, '🏅': Award, '🏥': Building2, '🔬': Activity, '💊': Pill, '💳': CreditCard, '📱': Phone, '🎥': Video };
  const Icon = map[emoji] || Activity;
  return <Icon size={24} />;
};

// ─── Types ──────────────────────────────────────────────────────────────────
interface FeaturedDoctor { name: string; qualifications?: string; specialty: string; years_exp: string; surgeries: string; rating: string; has_intro_video?: boolean; photo_url?: string; }
interface TestimonialSample { quote: string; name: string; age?: number; procedure?: string; facility?: string; star_rating?: number; visit_date?: string; is_pinned?: boolean; verified?: boolean; }
interface FaqItem { id: number; question: string; answer: string; }
interface VideoItem { id: number; title: string; video_type: string; video_url: string; duration: string | null; description: string | null; }
interface TrustSignal { id: number; emoji: string; name: string; type?: string; description?: string; active: boolean; }
interface ConditionTreatment { id: number; name: string; speciality_id?: number; speciality_name?: string; }
interface ProcTemplateData {
  procedure?: { id?: number; name: string; slug?: string; description?: string; emoji?: string; tagline?: string; };
  dept?: { id?: number; name: string; slug?: string; emoji?: string; };
  hospital?: { name?: string; address?: string; phone?: string; whatsapp?: string; hours_opd?: string; hours_emergency?: string; location_note?: string; };
  settings?: { dept_name?: string; cta_button_text?: string; whatsapp_number?: string; book_url?: string; is_live?: boolean; show_pricing?: boolean; show_videos?: boolean; };
  proof_numbers?: { value: string; label: string }[];
  featured_doctor: FeaturedDoctor | null;
  pricing?: { min_price: number | null; max_price: number | null; emi_available?: boolean; cashless_insurance?: boolean; cost_note?: string; };
  testimonials: { total: number; average_rating?: number; samples: TestimonialSample[]; };
  faqs?: { total: number; faq_list?: FaqItem[]; list?: FaqItem[]; };
  videos: { total: number; video_list?: VideoItem[]; };
  trust_signals?: { total?: number; active?: number; accreditations?: TrustSignal[]; why_us?: TrustSignal[]; };
  conditions_treatments?: ConditionTreatment[];
  all_testimonials?: TestimonialSample[]; faq_list?: FaqItem[]; video_list?: VideoItem[]; trust_signal_list?: TrustSignal[];
}

// ─── Font & Global ────────────────────────────────────────────────────────
const fontStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');
    body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
    .glass-panel { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(24px); border: 1px solid rgba(255, 255, 255, 0.5); }
  `} />
);

// ─── Components ───────────────────────────────────────────────────────────

function TrustBar() {
  return (
    <Box component={motion.div as any} initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} sx={{ bgcolor: 'rgba(255, 255, 255, 0.85)', borderBottom: `1px solid ${C.border}`, backdropFilter: 'blur(12px)', px: { xs: 2, md: 3 }, py: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.6, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 22, height: 22, bgcolor: C.navy, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={12} color="white" /></Box>
        <Typography sx={{ fontSize: 12, color: C.text2, fontWeight: 500 }}>Powered by <Box component="strong" sx={{ color: C.navy, fontWeight: 700 }}>Unofy Trust Link</Box></Typography>
      </Box>
    </Box>
  );
}

function HeroSection({ data, whatsappUrl }: { data: ProcTemplateData; whatsappUrl: string }) {
  const { procedure, hospital, settings, proof_numbers } = data;
  const procName = procedure?.name || 'Treatment';
  const tagline = procedure?.tagline || `Advanced ${procName} by specialized experts.`;
  const hospitalName = hospital?.name || 'Our Hospital';

  const defaultProof: any[] = [
    ...(data.featured_doctor ? [{ value: `${data.featured_doctor.surgeries}+`, label: 'Surgeries' }] : []),
    ...(data.testimonials?.average_rating ? [{ value: `${data.testimonials.average_rating}★`, label: 'Rating' }] : []),
  ];
  const validProofNumbers = (proof_numbers || []).filter((p) => p && p.value);
  const proofItems = validProofNumbers.length > 0 ? validProofNumbers : defaultProof;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: C.bg, px: { xs: 2.5, md: '48px' }, pt: { xs: 8, md: '120px' }, pb: { xs: 6, md: '100px' } }}>
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)', backgroundSize: '40px 40px', opacity: 0.6 }} />
      <Box sx={{ position: 'absolute', top: '-10%', left: '-10%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(37, 99, 235,0.06) 0%, transparent 60%)', filter: 'blur(40px)' }} />

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
        <Box component={motion.div as any} initial="hidden" animate="visible" variants={stagger} sx={{ flex: 1 }}>
          <motion.div variants={fadeUp}><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '999px', px: '14px', py: '6px', mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Activity size={14} color={C.text2} /><Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text2, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{hospitalName}</Typography></Box></motion.div>
          <motion.div variants={fadeUp}>
            <Typography variant="h1" sx={{ fontSize: { xs: 44, md: 68 }, fontWeight: 800, color: C.navy, lineHeight: 1.05, mb: 3, letterSpacing: '-0.03em' }}>
              Specialized <span style={{ background: `linear-gradient(135deg, ${C.teal} 0%, #06B6D4 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{procName}.</span>
            </Typography>
          </motion.div>
          <motion.div variants={fadeUp}><Typography sx={{ fontSize: { xs: 16, md: 18 }, color: C.text2, fontWeight: 400, lineHeight: 1.6, mb: 5, maxWidth: 480 }}>{tagline}</Typography></motion.div>
          <motion.div variants={fadeUp}>
            <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.navy, color: C.white, px: '28px', py: '16px', borderRadius: '16px', fontSize: 16, fontWeight: 700, textDecoration: 'none', transition: 'all 0.3s', boxShadow: `0 12px 24px -8px ${C.teal}`, '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 32px -8px ${C.teal}`, bgcolor: C.teal } }}>
                 {settings?.cta_button_text || `Book ${procName}`} <ArrowRight size={18} />
              </Box>
            </Box>
          </motion.div>
        </Box>
        <Box component={motion.div as any} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} sx={{ flex: 1, width: '100%' }}>
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: -20, background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(6,182,212,0.2) 100%)', filter: 'blur(30px)', borderRadius: '40px', zIndex: 0 }} />
            <Box className="glass-panel" sx={{ position: 'relative', zIndex: 1, borderRadius: '24px', p: 4, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4, border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.1)' }}>
              {proofItems.slice(0, 4).map((item, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 46 }, fontWeight: 800, color: C.navy, lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    {(item.value || '').replace(/[^0-9.]/g, '')}
                    <Box component="span" sx={{ color: C.teal, fontSize: '0.6em' }}>{(item.value || '').replace(/[0-9.]/g, '')}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: C.text2, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AccreditationsSection({ signals }: { signals: TrustSignal[] }) {
  const active = signals.filter(a => a.active);
  if (active.length === 0) return null;
  return (
    <Box sx={{ bgcolor: C.white, py: '40px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ textAlign: 'center', fontSize: 13, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 800, color: C.text3, mb: 5 }}>Recognized by Global Standards</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 3, md: 6 } }}>
          {active.map(a => (
            <motion.div key={a.id} variants={fadeUp}>
              <Box className="glass-panel" sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: 3, py: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.8)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', bgcolor: C.white } }}>
                <Award size={28} color={C.teal} />
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: C.navy }}>{a.name}</Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function AboutSection({ procedure }: { procedure: ProcTemplateData['procedure'] }) {
  if (!procedure?.description) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: '48px' }, textAlign: 'center' }}>
        <motion.div variants={fadeUp}>
           <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>About Procedure</Typography>
           <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '24px' }}>What is {procedure.name}?</Typography>
           <Typography sx={{ fontSize: 18, color: C.text2, lineHeight: 1.8 }}>{procedure.description}</Typography>
        </motion.div>
      </Box>
    </Box>
  );
}

function TreatmentsSection({ items }: { items: ConditionTreatment[] }) {
  if (!items || items.length === 0) return null;
  // Group treatments by their speciality
  const groups: Record<string, ConditionTreatment[]> = {};
  items.forEach((it) => {
    const key = it.speciality_name || 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(it);
  });
  const groupKeys = Object.keys(groups);
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Our Expertise</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Treatments &amp; Procedures</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 3 }}>
          {groupKeys.map((key) => (
            <Box component={motion.div as any} variants={fadeUp} key={key} sx={{ border: `1px solid ${C.border}`, borderRadius: '24px', p: '28px', bgcolor: C.white, transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 20px 40px -12px ${C.border}`, borderColor: 'rgba(37,99,235,0.3)' } }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', color: C.teal }}>
                <Activity size={22} />
              </Box>
              <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 17, fontWeight: 800, color: C.navy, mb: '16px', textTransform: 'capitalize' }}>{key.toLowerCase()}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groups[key].map((t) => (
                  <Box key={t.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.teal, flexShrink: 0, mt: '7px' }} />
                    <Typography sx={{ fontSize: 14, color: C.text2, fontWeight: 500, textTransform: 'capitalize' }}>{t.name.toLowerCase()}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function PricingSection({ pricing }: { pricing: ProcTemplateData['pricing'] }) {
  if (!pricing || (!pricing.min_price && !pricing.max_price)) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 } }}>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Transparent Pricing</Typography>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 48 }, fontWeight: 800, color: C.navy, mb: '20px', lineHeight: 1.1 }}>Treatment Cost Estimator</Typography>
            <Typography sx={{ fontSize: 18, color: C.text2, mb: '32px' }}>We believe in complete transparency. Our packages are designed to give you peace of mind with no hidden surprises.</Typography>
          </Box>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
             <Box sx={{ bgcolor: C.tealLt, border: `1px solid ${C.teal}30`, borderRadius: '24px', p: '32px', position: 'relative', overflow: 'hidden' }}>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
                 <Typography sx={{ fontSize: 14, textTransform: 'uppercase', fontWeight: 700, color: C.teal }}>Estimated Cost</Typography>
                 <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 40, fontWeight: 800, color: C.navy }}>{pricing.min_price ? formatPrice(pricing.min_price) : ''} {pricing.min_price && pricing.max_price ? ' - ' : ''} {pricing.max_price ? formatPrice(pricing.max_price) : ''}</Typography>
               </Box>
               
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                 {pricing.emi_available && (
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', bgcolor: C.white, p: '16px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                     <Banknote color={C.teal} />
                     <Box>
                       <Typography sx={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>0% EMI Available</Typography>
                       <Typography sx={{ fontSize: 13, color: C.text2 }}>Easy monthly instalments</Typography>
                     </Box>
                   </Box>
                 )}
                 {pricing.cashless_insurance && (
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', bgcolor: C.white, p: '16px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                     <ShieldCheck color={C.teal} />
                     <Box>
                       <Typography sx={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>Cashless Insurance</Typography>
                       <Typography sx={{ fontSize: 13, color: C.text2 }}>Direct claims with all major TPAs</Typography>
                     </Box>
                   </Box>
                 )}
               </Box>
               {pricing.cost_note && (
                 <Typography sx={{ fontSize: 12, color: C.text3, mt: 3, fontStyle: 'italic' }}>* {pricing.cost_note}</Typography>
               )}
             </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SurgeonSection({ doctor }: { doctor: FeaturedDoctor }) {
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: -15, background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(6,182,212,0.15) 100%)', borderRadius: '32px', zIndex: 0, transform: 'rotate(-3deg)' }} />
            <Box component="img" src={doctor.photo_url || 'https://via.placeholder.com/600'} sx={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: '24px', position: 'relative', zIndex: 1, boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)' }} />
            <Box className="glass-panel" sx={{ position: 'absolute', bottom: -20, right: -20, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)', p: '16px 24px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}>
              <Box sx={{ width: 40, height: 40, bgcolor: C.goldLt, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Star size={20} color={C.gold} fill={C.gold} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, lineHeight: 1 }}>{doctor.rating || '4.9'} <span style={{fontSize: 14, color: C.text3}}>/5</span></Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text2 }}>Highly Rated</Typography>
              </Box>
            </Box>
          </Box>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Lead Surgeon</Typography>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 48 }, fontWeight: 800, color: C.navy, mb: '12px', lineHeight: 1.1 }}>{doctor.name}</Typography>
            <Typography sx={{ fontSize: 18, color: C.text2, mb: '8px' }}>{doctor.qualifications}</Typography>
            <Typography sx={{ fontSize: 16, color: C.teal, fontWeight: 600, mb: '32px' }}>{doctor.specialty}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 3 }}>
              {[ { value: `${doctor.years_exp}+`, label: 'Years Exp' }, { value: `${doctor.surgeries}+`, label: 'Surgeries' } ].map((stat, i) => (
                <Box key={i}>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 28, fontWeight: 800, color: C.navy }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 12, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{stat.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function VideosSection({ videos }: { videos: VideoItem[] }) {
  if (!videos || videos.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Library</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Watch &amp; Learn</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, gap: 4 }}>
          {videos.slice(0,4).map((v) => (
            <motion.div variants={fadeUp} key={v.id}>
              <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '24px', overflow: 'hidden', bgcolor: C.bg }}>
                 <Box sx={{ aspectRatio: '16/9', bgcolor: C.navy, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                   <Video size={40} color="rgba(255,255,255,0.2)" />
                 </Box>
                 <Box sx={{ p: 3 }}>
                   <Typography sx={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: C.teal, bgcolor: C.tealLt, px: '10px', py: '4px', borderRadius: '8px', mb: 2 }}>{v.video_type || 'Explanatory'}</Typography>
                   <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 18, fontWeight: 700, color: C.navy, mb: '8px' }}>{v.title}</Typography>
                   {v.description && <Typography sx={{ fontSize: 14, color: C.text2 }}>{v.description}</Typography>}
                 </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection({ faqs }: { faqs: FaqItem[] }) {
  if (!faqs || faqs.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ textAlign: 'center', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>FAQ</Typography>
        <Typography sx={{ textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Frequently Asked Questions</Typography>
        <Box>
          {faqs.map((f, i) => (
            <motion.div variants={fadeUp} key={f.id}>
              <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${C.border}` }}>
                <AccordionSummary expandIcon={<ChevronDown size={20} color={C.text2} />} sx={{ px: 0, py: 2 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.navy }}>{f.question}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pb: 3 }}>
                  <Typography sx={{ fontSize: 15, color: C.text2, lineHeight: 1.6 }}>{f.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default function ProcLinkTemplate({ initialData }: { initialData?: ProcTemplateData }) {
  const { deptId, procId } = useParams();
  const [data, setData] = useState<ProcTemplateData | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialData || !deptId || !procId) {
      if(!initialData) setLoading(false);
      return;
    }
    const dId = Number(deptId);
    const pId = Number(procId);

    Promise.all([
      hospitalApiService.getProcPreview(dId, pId).catch(() => null)
    ])
      .then(([previewRes]) => {
        if (!previewRes) { setError(true); return; }
        setData(previewRes.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [deptId, procId, initialData]);

  if (loading) return <Box sx={{ bgcolor: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{fontStyles}<CircularProgress sx={{ color: C.teal }} /></Box>;
  if (error || !data) return <Box sx={{ bgcolor: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>{fontStyles}<Typography fontSize={48}>😕</Typography><Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 24, fontWeight: 800, color: C.navy }}>Link not found</Typography></Box>;

  const { hospital, faqs, videos, trust_signals, featured_doctor, pricing } = data;
  const whatsappUrl = hospital?.whatsapp ? `https://wa.me/${hospital.whatsapp.replace(/\D/g, '')}` : '#';
  const accreditations: TrustSignal[] = trust_signals?.accreditations || [];

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
      {fontStyles}
      <TrustBar />
      <HeroSection data={data} whatsappUrl={whatsappUrl} />
      {accreditations.length > 0 && <AccreditationsSection signals={accreditations} />}
      <AboutSection procedure={data.procedure} />
      <TreatmentsSection items={data.conditions_treatments || []} />
      {featured_doctor && <SurgeonSection doctor={featured_doctor} />}
      <PricingSection pricing={pricing} />
      <VideosSection videos={videos?.video_list || []} />
      <FaqSection faqs={faqs?.faq_list || faqs?.list || []} />
      <Box sx={{ py: 4, textAlign: 'center', bgcolor: C.navy, borderTop: `1px solid ${C.navy3}` }}>
        <Typography sx={{ fontSize: 13, color: C.text3 }}>&copy; {new Date().getFullYear()} {hospital?.name}. All rights reserved.</Typography>
      </Box>
    </Box>
  );
}
